import { autoinject, observable } from 'aurelia-framework';
import { Router, RouteConfig } from 'aurelia-router';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { AssistanceService } from 'app/shared/services/assistance-service';
import { State } from 'app/shared/lib/state';
import { Toaster } from 'app/shared/lib/toaster';
import { ApiError } from 'app/shared/lib/api/api-types';
import { LoginContext } from 'app/shared/lib/login-context';
import { VerifyBy } from 'app/shared/models/assistance-models';
import { PasswordService } from 'app/core/services/password-service';
import { I18N } from 'aurelia-i18n';
import { guid } from 'app/shared/lib/guid';

@autoinject()
export class ChangePasswordViewModel {

    public method: VerifyBy = 'email';
    public code: string = '';
    @observable({ changeHandler: 'clearErrorMessage' })
    public newPassword: string = '';
    public loginName: string = '';
    @observable({ changeHandler: 'clearErrorMessage' })
    public confirmPassword: string = '';
    public errorMessage: string = '';
    protected id: guid = guid();
    protected readonly state: State = new State();

    constructor(
        private readonly toaster: Toaster,
        private readonly router: Router,
        private readonly logger: AppLogger,
        private readonly loginContext: LoginContext,
        protected readonly passwordService: PasswordService,
        private readonly i18n: I18N,
        private readonly service: AssistanceService) {
        this.logger.source = this;
        this.logger.target = 'console';
    }

    protected async activate(params: any, config: RouteConfig): Promise<void> {
        const { code } = params;
        if(!this.loginName && this.loginContext.loginName) {
            this.loginName = this.loginContext.loginName;
        }
        if(code !== undefined) {
            params.code = null;
            this.router.navigateToRoute(config.name!, params, { trigger: false, replace: true });
            if(code) {
                this.code = code;
            }
            await this.retrieveLoginNames();
        }
    }

    protected process(): void {
        this.changePassword();
    }

    protected passwordsMatch(password1: string, password2: string): boolean {
        if(password1) { return password1 === password2; }
        return !password2;

    }

    private async retrieveLoginNames(): Promise<void> {
        if(!this.loginContext.loginName) {
            try {
                const { logins } = await this.service.retrieveLoginNames(this.code);
                if(!logins || logins.length != 1) {
                    throw Error();
                } else {
                    this.loginContext.loginName = logins[0].loginName!;
                }
                this.loginName = this.loginContext.loginName;
            }
            catch(e) {
                if(e instanceof ApiError) {
                    if(e.hasError('request.failedAuthentication')) {
                        this.errorMessage = this.i18n.tr('ui:messages.errors.failedAuthentication');
                        this.toaster.error().message(this.errorMessage).show();
                        return;
                    }
                }
            }
            finally {
            }
        }
    }

    private async changePassword(): Promise<void> {
        try {
            this.state.beginSave();

            const response = await this.service.changePasswordByCode(this.code, this.newPassword);
            if(response.isSuccessful) {
                this.code = '';
                this.loginContext.password = this.newPassword;
                // retrieve login name from response
                if(response.logins && response.logins.length > 0) {
                    this.loginContext.loginName = response.logins[0].loginName || '';
                    this.toaster.info().message(this.i18n.tr('core:local.changePassword.passwordChangedSuccessfully', { what: this.loginContext.loginName })).show();
                }
                else {
                    // QUESTION [Matt] Why would we want to set password and clear login name?
                    this.loginContext.loginName = '';
                    this.toaster.info().message(this.i18n.tr('core:local.changePassword.passwordChangedSuccessfullyShort')).show();
                }
                this.errorMessage = '';
                // route back to login page
                this.router.navigateToRoute('checkin');
            }
            else {
                this.errorMessage = this.i18n.tr('core:local.changePassword.unableToChangePassword');
            }
        }
        catch(e) {
            if(e instanceof ApiError) {
                if(e.hasError('password.previouslyUsed')) {
                    this.errorMessage = this.i18n.tr('core:local.changePassword.cannotRepeatPassword');
                    return;
                }
                else if(e.hasError('password.locked')) {
                    this.errorMessage = this.i18n.tr('core:local.changePassword.passwordForAccountCannotBeChanged');
                    return;
                } else if(e.hasError('request.failedAuthentication')) {
                    this.errorMessage = this.i18n.tr('ui:messages.errors.failedAuthentication');
                    this.toaster.error().message(this.errorMessage).show();
                    return;
                } else if(e.hasError('request.failedAuthentication')) {
                    this.errorMessage = this.i18n.tr('ui:messages.errors.failedAuthentication');
                    this.toaster.error().message(this.errorMessage).show();
                    return;
                }
            }
            this.errorMessage = this.i18n.tr('core:local.changePassword.unableToChangePassword');
        }
        finally {
            this.state.endSave();
        }
    }

    protected clearErrorMessage(): void {
        this.errorMessage = '';
    }
}
